/**
 * Mealtime list builder - a line-for-line port of
 * ucDietech_Report_Generator_clbMealtime.vb `Fill_clbMeals` (.vb:70-140), which is
 * what every report's mealtime checklist is filled from.
 *
 * Kept as a pure function so the single-facility and multi-facility paths share one
 * implementation, exactly as WinForms shares one control.
 */

/** Mealtime ordinals, per FacilitySettings.Get_MealNames (.vb:6361). */
export const MEALTIME_BREAKFAST = 0;
export const MEALTIME_EAM = 3;
export const MEALTIME_CUSTOM = 9;

/**
 * Fallback labels used when two facilities name the same mealtime DIFFERENTLY.
 * WinForms `GetGenericMealName` (.vb:150-154) - note the index IS the ordinal, so
 * PM2 (7) and HS2 (8) sit after HS (6) here even though the facility's own display
 * order may interleave them.
 */
const GENERIC_NAMES = ['Breakfast', 'Lunch', 'Dinner', 'EAM', 'AM', 'PM', 'HS', 'PM2', 'HS2'];

/** The per-facility fields the builder needs. Matches the API's FacilityReportInfo. */
export interface MealtimeFacility {
  pKey_Facility: number;
  useProduction: boolean;
  break_Name: string | null;
  lunch_Name: string | null;
  dinner_Name: string | null;
  nourish_Name_EAM: string | null;
  nourish_Name_AM: string | null;
  nourish_Name_PM: string | null;
  nourish_Name_HS: string | null;
  nourish_Name_PM2: string | null;
  nourish_Name_HS2: string | null;
  nourish_EAM_SortOrder: number;
  nourish_AM_SortOrder: number;
  nourish_PM_SortOrder: number;
  nourish_HS_SortOrder: number;
  nourish_PM2_SortOrder: number;
  nourish_HS2_SortOrder: number;
}

export interface MealtimeOptionsConfig {
  /** Ordinals 0-2. WinForms `IncludeMainMealtimes`. */
  includeMain: boolean;
  /** Ordinals 3-8. WinForms `IncludeNourishments`. */
  includeNourishments: boolean;
  /** Lets EAM through on NON-production facilities. WinForms `ShowAllNourishmentInLite`. */
  showAllNourishmentInLite?: boolean;
  /** Appends ordinal 9 "Custom". WinForms `IncludeCustom`. */
  includeCustom?: boolean;
}

export interface MealtimeOption {
  value: number;
  label: string;
}

const str = (v: string | null | undefined) => (v ?? '').trim();

/**
 * One facility's mealtimes in DISPLAY order, mirroring Get_MealNames.
 *
 * Main meals come first in ordinal order, then the six nourishments reseated by the
 * facility's `Nourish_*_SortOrder` (`lstRet(SortOrder + 3) = ...`). The nourishment
 * block is therefore NOT in ordinal order - 0-based sort values 0..5 decide it.
 */
function orderedMealtimes(f: MealtimeFacility): Array<{ ordinal: number; name: string }> {
  const main = [
    { ordinal: 0, name: str(f.break_Name) },
    { ordinal: 1, name: str(f.lunch_Name) },
    { ordinal: 2, name: str(f.dinner_Name) },
  ];

  // [sortOrder, ordinal, name] for the six nourishments.
  const nourishments: Array<[number, number, string]> = [
    [f.nourish_EAM_SortOrder, 3, str(f.nourish_Name_EAM)],
    [f.nourish_AM_SortOrder, 4, str(f.nourish_Name_AM)],
    [f.nourish_PM_SortOrder, 5, str(f.nourish_Name_PM)],
    [f.nourish_HS_SortOrder, 6, str(f.nourish_Name_HS)],
    [f.nourish_PM2_SortOrder, 7, str(f.nourish_Name_PM2)],
    [f.nourish_HS2_SortOrder, 8, str(f.nourish_Name_HS2)],
  ];

  // Stable sort by the configured slot; equal/garbage values keep ordinal order so a
  // facility with every sort order at 0 still lists EAM, AM, PM, HS, PM2, HS2.
  nourishments.sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));

  return [...main, ...nourishments.map(([, ordinal, name]) => ({ ordinal, name }))];
}

/**
 * Builds the checklist for one or more facilities.
 *
 * Inclusion rules, verbatim from Fill_clbMeals:
 *   - ordinal < 3  -> `includeMain`
 *   - ordinal == 3 -> `(useProduction OR showAllNourishmentInLite) AND includeNourishments`
 *   - otherwise    -> `includeNourishments`
 *   - and in EVERY branch, only when the facility's name for it is non-empty. A
 *     mealtime the facility does not use is omitted, not blank.
 *
 * Multi-facility: union by ordinal. When a second facility names an ordinal
 * DIFFERENTLY, the label falls back to the generic English name - WinForms replaces
 * the row's MealName with GetGenericMealName rather than showing one site's wording
 * for both.
 *
 * `useProduction` is evaluated PER facility here. WinForms reads it once from the
 * control's own facility, so a non-production facility added to a production one
 * would show EAM there; evaluating it per facility is the behaviour the rule plainly
 * intends and avoids a mealtime that site cannot serve.
 */
export function buildMealtimeOptions(
  facilities: MealtimeFacility[],
  config: MealtimeOptionsConfig,
): MealtimeOption[] {
  const { includeMain, includeNourishments, showAllNourishmentInLite, includeCustom } = config;

  // Ordinal -> label, insertion-ordered like the DataTable it replaces.
  const byOrdinal = new Map<number, string>();
  const order: number[] = [];

  for (const f of facilities) {
    for (const { ordinal, name } of orderedMealtimes(f)) {
      // Unnamed mealtime - the facility does not use it.
      if (name === '') continue;

      if (ordinal < MEALTIME_EAM) {
        if (!includeMain) continue;
      } else if (ordinal === MEALTIME_EAM) {
        if (!((f.useProduction || showAllNourishmentInLite) && includeNourishments)) continue;
      } else if (!includeNourishments) {
        continue;
      }

      const existing = byOrdinal.get(ordinal);
      if (existing === undefined) {
        byOrdinal.set(ordinal, name);
        order.push(ordinal);
      } else if (existing !== name) {
        // Two facilities, two names -> neither wins.
        byOrdinal.set(ordinal, GENERIC_NAMES[ordinal] ?? name);
      }
    }
  }

  if (includeCustom && !byOrdinal.has(MEALTIME_CUSTOM)) {
    byOrdinal.set(MEALTIME_CUSTOM, 'Custom');
    order.push(MEALTIME_CUSTOM);
  }

  return order.map((ordinal) => ({ value: ordinal, label: byOrdinal.get(ordinal) as string }));
}

/**
 * Adapts the current facility's settings record (globalStore.facilitySettings, i.e. a
 * full TblFacility) to the builder's input. Field names differ only in casing from
 * the API's FacilityReportInfo, so both sources feed one code path.
 */
export function facilitySettingsToMealtimeFacility(
  fs: Record<string, unknown> | null,
  facilityId: number,
): MealtimeFacility | null {
  if (!fs) return null;
  const s = (k: string) => (typeof fs[k] === 'string' ? (fs[k] as string) : '');
  const n = (k: string) => (typeof fs[k] === 'number' ? (fs[k] as number) : 0);
  return {
    pKey_Facility: facilityId,
    useProduction: Boolean(fs.useProduction),
    break_Name: s('break_Name'),
    lunch_Name: s('lunch_Name'),
    dinner_Name: s('dinner_Name'),
    nourish_Name_EAM: s('nourish_Name_EAM'),
    nourish_Name_AM: s('nourish_Name_AM'),
    nourish_Name_PM: s('nourish_Name_PM'),
    nourish_Name_HS: s('nourish_Name_HS'),
    nourish_Name_PM2: s('nourish_Name_PM2'),
    nourish_Name_HS2: s('nourish_Name_HS2'),
    nourish_EAM_SortOrder: n('nourish_EAM_SortOrder'),
    nourish_AM_SortOrder: n('nourish_AM_SortOrder'),
    nourish_PM_SortOrder: n('nourish_PM_SortOrder'),
    nourish_HS_SortOrder: n('nourish_HS_SortOrder'),
    nourish_PM2_SortOrder: n('nourish_PM2_SortOrder'),
    nourish_HS2_SortOrder: n('nourish_HS2_SortOrder'),
  };
}
